/**
 * Entry point. Registers the Alpine.js `biblePlayer` component and starts Alpine.
 *
 * State model:
 *   listIndex    — index into LISTS (-1 = nothing selected)
 *   bookIndex    — index into activeList.books
 *   chapterIndex — 0-based index into chapters[]
 *   chapters     — array of audio URLs for the current book (populated by _loadCurrentBook)
 *
 * Playback auto-advances: chapter → book (within list) → list end (resets to ch 0).
 * Progress is persisted to localStorage so the position survives page reloads.
 * Cast state is managed by cast.js; when a Cast session is active the local <audio> is silent.
 */
import Alpine from 'alpinejs'
import config from './config.js'
import { LISTS, getBookChapterUrls } from './books.js'
import { initCast, castList, stopCast } from './cast.js'
import '../css/main.css'

window.Alpine = Alpine

// localStorage key for { listIndex, bookIndex, chapterIndex }
const STORAGE_KEY = 'biblePlayerState'

Alpine.data('biblePlayer', () => ({
  LISTS,

  listIndex: -1,
  bookIndex: 0,
  chapterIndex: 0,

  chapters: [],
  isPlaying: false,
  audio: null,

  castAvailable: false,
  isCasting: false,

  get activeList() {
    return this.listIndex >= 0 ? LISTS[this.listIndex] : null
  },

  get currentBookName() {
    return this.activeList ? this.activeList.books[this.bookIndex] : ''
  },

  get totalChapters() {
    return this.chapters.length
  },

  get currentChapter() {
    return this.chapterIndex + 1
  },

  get chapterProgressPercent() {
    if (this.totalChapters === 0) return 0
    return Math.round((this.currentChapter / this.totalChapters) * 100)
  },

  get totalBooksInList() {
    return this.activeList ? this.activeList.books.length : 0
  },

  get bookNumberInList() {
    return this.bookIndex + 1
  },

  get nextBookName() {
    if (!this.activeList) return ''
    const next = this.bookIndex + 1
    return next < this.activeList.books.length ? this.activeList.books[next] : ''
  },

  selectList(index) {
    const wasPlaying = this.isPlaying
    this._stopAudio()
    this.listIndex = index
    this.bookIndex = 0
    this.chapterIndex = 0
    this._loadCurrentBook()
    this._saveProgress()
    if (wasPlaying) this._playCurrentChapter()
  },

  selectBook(bookIdx) {
    const wasPlaying = this.isPlaying
    this._stopAudio()
    this.bookIndex = bookIdx
    this.chapterIndex = 0
    this._loadCurrentBook()
    this._saveProgress()
    if (wasPlaying) this._playCurrentChapter()
  },

  selectChapter(chapterIdx) {
    const wasPlaying = this.isPlaying
    this._stopAudio()
    this.chapterIndex = chapterIdx
    this._saveProgress()
    if (wasPlaying) this._playCurrentChapter()
  },

  togglePlay() {
    if (!this.activeList || this.chapters.length === 0) return
    if (this.isPlaying) {
      this.audio.pause()
      this.isPlaying = false
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused'
    } else {
      this._playCurrentChapter()
    }
  },

  prevChapter() {
    if (!this.activeList) return
    const wasPlaying = this.isPlaying
    this._stopAudio()
    if (this.chapterIndex > 0) {
      this.chapterIndex--
    } else if (this.bookIndex > 0) {
      this.bookIndex--
      this._loadCurrentBook()
      this.chapterIndex = this.chapters.length - 1
    } else {
      if (wasPlaying) this._playCurrentChapter()
      return
    }
    this._saveProgress()
    if (wasPlaying) this._playCurrentChapter()
  },

  nextChapter() {
    if (!this.activeList) return
    const wasPlaying = this.isPlaying
    this._stopAudio()
    if (this.chapterIndex < this.totalChapters - 1) {
      this.chapterIndex++
    } else if (this.bookIndex < this.activeList.books.length - 1) {
      this.bookIndex++
      this.chapterIndex = 0
      this._loadCurrentBook()
    } else {
      if (wasPlaying) this._playCurrentChapter()
      return
    }
    this._saveProgress()
    if (wasPlaying) this._playCurrentChapter()
  },

  _loadCurrentBook() {
    this.chapters = getBookChapterUrls(this.currentBookName, config.storage.getBaseUrl())
  },

  _stopAudio() {
    if (this.audio) this.audio.pause()
    this.isPlaying = false
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused'
  },

  _playCurrentChapter() {
    if (!this.audio) {
      this.audio = new Audio()
      this.audio.addEventListener('ended', () => this._onChapterEnded())
      this.audio.addEventListener('error', () => { this.isPlaying = false })
      this.audio.addEventListener('loadedmetadata', () => this._updatePositionState())
    }
    this.audio.src = this.chapters[this.chapterIndex]
    this.audio.play()
    this.isPlaying = true
    this._updateMediaSession()
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing'
  },

  // Auto-advance chain: next chapter → next book in list → end of list (reset + stop).
  _onChapterEnded() {
    if (this.chapterIndex < this.chapters.length - 1) {
      this.chapterIndex++
      this._saveProgress()
      this._playCurrentChapter()
    } else if (this.activeList && this.bookIndex < this.activeList.books.length - 1) {
      this.bookIndex++
      this.chapterIndex = 0
      this._loadCurrentBook()
      this._saveProgress()
      this._playCurrentChapter()
    } else {
      this.isPlaying = false
      this.chapterIndex = 0
      this._saveProgress()
    }
  },

  _updateMediaSession() {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.metadata = new MediaMetadata({
      title: `${this.currentBookName} ${this.chapterIndex + 1}`,
      artist: 'ESV Audio Bible',
      album: this.activeList ? this.activeList.name : '',
      artwork: [
        { src: '/icon/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
        { src: '/icon/android-chrome-512x512.png', sizes: '512x512', type: 'image/png' },
      ],
    })
  },

  _updatePositionState() {
    if (!('mediaSession' in navigator) || !this.audio) return
    if (!this.audio.duration || isNaN(this.audio.duration)) return
    try {
      navigator.mediaSession.setPositionState({
        duration: this.audio.duration,
        playbackRate: this.audio.playbackRate,
        position: this.audio.currentTime,
      })
    } catch {} // setPositionState throws in some browsers if values are inconsistent
  },

  _initMediaSessionHandlers() {
    if (!('mediaSession' in navigator)) return
    const handlers = [
      ['play', () => { if (!this.isPlaying) this.togglePlay() }],
      ['pause', () => { if (this.isPlaying) this.togglePlay() }],
      ['previoustrack', () => this.prevChapter()],
      ['nexttrack', () => this.nextChapter()],
      ['seekbackward', (details) => {
        if (!this.audio) return
        this.audio.currentTime = Math.max(0, this.audio.currentTime - (details.seekOffset || 10))
        this._updatePositionState()
      }],
      ['seekforward', (details) => {
        if (!this.audio) return
        this.audio.currentTime = Math.min(this.audio.duration, this.audio.currentTime + (details.seekOffset || 10))
        this._updatePositionState()
      }],
      ['seekto', (details) => {
        if (!this.audio) return
        this.audio.currentTime = details.seekTime
        this._updatePositionState()
      }],
    ]
    for (const [action, handler] of handlers) {
      try { navigator.mediaSession.setActionHandler(action, handler) } catch {} // not all browsers support all actions
    }
  },

  castCurrentList() {
    if (this.isCasting) {
      stopCast()
      return
    }
    if (!this.activeList) return
    const startTime = this.audio ? this.audio.currentTime : 0
    castList(this.activeList.books, this.bookIndex, this.chapterIndex, startTime)
  },

  _saveProgress() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      listIndex: this.listIndex,
      bookIndex: this.bookIndex,
      chapterIndex: this.chapterIndex,
    }))
  },

  _restoreProgress() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
      if (!saved || saved.listIndex < 0 || saved.listIndex >= LISTS.length) return
      this.listIndex = saved.listIndex
      const list = LISTS[this.listIndex]
      this.bookIndex = Math.min(saved.bookIndex || 0, list.books.length - 1)
      this._loadCurrentBook()
      this.chapterIndex = Math.min(saved.chapterIndex || 0, this.chapters.length - 1)
    } catch {}
  },

  isDark() {
    return document.documentElement.getAttribute('data-theme') === 'night'
  },

  toggleTheme() {
    const next = this.isDark() ? 'winter' : 'night'
    document.documentElement.setAttribute('data-theme', next)
    localStorage.setItem('theme', next)
  },

  init() {
    const saved = localStorage.getItem('theme') || 'winter'
    document.documentElement.setAttribute('data-theme', saved)
    this._restoreProgress()
    this._initMediaSessionHandlers()
    initCast({
      onAvailable: (v) => { this.castAvailable = v },
      onSessionChange: (v) => {
        this.isCasting = v
        if (v) this._stopAudio()
      },
    })
  },
}))

Alpine.start()
